import {Box, HStack, Icon, Link, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

import {EMPTY_PLACEHOLDER} from '../../constants/placeholders';
import {getFileIcon} from '../../utils/getFileIcon';
import {MessageText} from '../EmailLeadPreviewDialog/MessageText';

export interface SentMessageAttachment {
  id: string;
  title: string;
  remoteUrl: string;
  contentType?: string | null;
}

export interface SentMessagePreviewLinkedValue {
  label: string;
  href?: string;
}

export interface SentMessagePreviewDialogProps {
  body: string;
  recipient: string;
  sender: string;
  sentAt: string;
  subject: string;
  attachments?: SentMessageAttachment[];
  record?: SentMessagePreviewLinkedValue;
  recordReasoning?: string;
  customer?: SentMessagePreviewLinkedValue;
  customerReasoning?: string;
}

export function SentMessagePreviewDialog(props: SentMessagePreviewDialogProps) {
  const formatDateTime = useDateTimeFormatter();

  return (
    <HStack spacing={4} height="100%">
      <Box
        flexGrow={1}
        maxHeight="100%"
        width="70%"
        backgroundColor="palettes.neutral.20.100"
        borderRadius="medium"
        padding={4}
        overflowY="auto"
      >
        <HStack justify="center" align="center">
          <Box height="100%" width="100%" padding={10} backgroundColor="general.white">
            <MessageText htmlData={props.body} />
          </Box>
        </HStack>
      </Box>

      <Box
        flexGrow={1}
        width="30%"
        backgroundColor="palettes.neutral.20.100"
        borderRadius="medium"
        padding={4}
      >
        <VStack spacing={4}>
          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.email.labels.subject')}
            </Text>
            <Text size="small">{props.subject}</Text>
          </VStack>

          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.email.labels.recipient')}
            </Text>
            <Text size="small">{props.recipient}</Text>
          </VStack>

          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.email.labels.sender')}
            </Text>
            <Text size="small">{props.sender}</Text>
          </VStack>

          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.email.labels.sentAt')}
            </Text>
            <Text size="small">
              {props.sentAt === EMPTY_PLACEHOLDER
                ? props.sentAt
                : formatDateTime('dateTimeShort', parseDate(props.sentAt))}
            </Text>
          </VStack>

          <Show when={props.attachments && props.attachments.length > 0 ? props.attachments : null}>
            {(attachments) => (
              <VStack spacing={1}>
                <Text size="xSmall" color="secondary">
                  {i18n.t('entity.email.labels.attachments')}
                </Text>
                <VStack spacing={1}>
                  {attachments.map((attachment) => (
                    <HStack spacing={1} align="center" key={attachment.id}>
                      <Icon value={getFileIcon(attachment.contentType)} size={4} />
                      <Link
                        href={attachment.remoteUrl}
                        target="_blank"
                        size="small"
                        title={attachment.title}
                      />
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            )}
          </Show>

          <LinkedValueRow
            label={i18n.t('page.myProfile.receivingMailbox.syncedMessages.record')}
            value={props.record}
          />

          <Show when={props.recordReasoning}>
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('page.myProfile.receivingMailbox.syncedMessages.recordReasoning')}
              </Text>
              <Text size="small">{props.recordReasoning}</Text>
            </VStack>
          </Show>

          <LinkedValueRow label={i18n.t('general.labels.customer')} value={props.customer} />

          <Show when={props.customerReasoning}>
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('page.myProfile.receivingMailbox.syncedMessages.customerReasoning')}
              </Text>
              <Text size="small">{props.customerReasoning}</Text>
            </VStack>
          </Show>
        </VStack>
      </Box>
    </HStack>
  );
}

interface LinkedValueRowProps {
  label: string;
  value?: SentMessagePreviewLinkedValue;
}

function LinkedValueRow(props: LinkedValueRowProps) {
  if (isNil(props.value)) {
    return null;
  }

  return (
    <VStack spacing={1}>
      <Text size="xSmall" color="secondary">
        {props.label}
      </Text>
      {isNotNil(props.value.href) ? (
        <Link href={props.value.href} target="_blank" size="small" title={props.value.label} />
      ) : (
        <Text size="small">{props.value.label}</Text>
      )}
    </VStack>
  );
}
